import { Suspense, useEffect, useRef } from 'react'
import { motion, MotionConfig, useReducedMotion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { Outlet, useLocation } from 'react-router-dom'

import { getPublicRouteMeta } from '@/content/publicRouteMeta'
import Footer from './Footer'
import Header from './Header'
import ProductReleaseNotice from './ProductReleaseNotice'
import LoadingSpinner from '@components/ui/LoadingSpinner'
import { routeTransition } from '@utils/animations'

function PublicContentFallback() {
  return (
    <div
      className="flex min-h-[45vh] items-center justify-center bg-[var(--x3-page)]"
      aria-busy="true"
      aria-label="Loading page"
    >
      <LoadingSpinner size="lg" />
    </div>
  )
}

function PublicRouteLayout() {
  const location = useLocation()
  const reduceMotion = useReducedMotion()
  const mainRef = useRef(null)
  const previousPathnameRef = useRef(location.pathname)
  const routeMeta = getPublicRouteMeta(location.pathname)
  const canonicalUrl = routeMeta.canonicalPath
    ? `https://x3algo.com${routeMeta.canonicalPath}`
    : null

  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) return

    previousPathnameRef.current = location.pathname
    if (location.hash) return

    const focusFrame = window.requestAnimationFrame(() => {
      mainRef.current?.focus({ preventScroll: true })
    })

    return () => window.cancelAnimationFrame(focusFrame)
  }, [location.hash, location.pathname])

  return (
    <MotionConfig reducedMotion="user">
      <div className="flex min-h-screen flex-col bg-[var(--x3-page)] text-[var(--x3-text)]">
        <Helmet>
          <title>{routeMeta.title}</title>
          <meta name="description" content={routeMeta.description} />
          <meta property="og:title" content={routeMeta.title} />
          <meta property="og:description" content={routeMeta.description} />
          <meta property="og:type" content="website" />
          <meta property="og:image" content="https://x3algo.com/og-brand.png" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content="x3Algo" />
          <meta property="og:site_name" content="x3Algo" />
          <meta property="og:locale" content="en_IN" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={routeMeta.title} />
          <meta name="twitter:description" content={routeMeta.description} />
          <meta name="twitter:image" content="https://x3algo.com/og-brand.png" />
          <meta name="twitter:image:alt" content="x3Algo" />
          {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
          {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        </Helmet>
        <Header />
        <ProductReleaseNotice />
        <main
          ref={mainRef}
          id="main-content"
          className="min-w-0 flex-1 focus:outline-none"
          tabIndex={-1}
        >
          <Suspense fallback={<PublicContentFallback />}>
            <motion.div
              key={location.pathname}
              initial={reduceMotion ? false : { opacity: 0.96 }}
              animate={{ opacity: 1 }}
              transition={reduceMotion ? { duration: 0 } : routeTransition}
            >
              <Outlet />
            </motion.div>
          </Suspense>
        </main>
        <Footer />
      </div>
    </MotionConfig>
  )
}

export default PublicRouteLayout
